import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react"
import { BulkRejectionProgressDto } from "@/modules/recruitment/applications/applicants-service"

export interface BulkRejectionJob {
  jobId: string
  total: number
  processed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
}

interface BulkRejectionContextValue {
  jobs: BulkRejectionJob[]
  /** Count of jobs that are still Pending or InProgress */
  activeCount: number
  addJob: (job: BulkRejectionJob) => void
  updateJob: (progress: BulkRejectionProgressDto) => void
  clearCompleted: () => void
}

const BulkRejectionContext = createContext<BulkRejectionContextValue | null>(null)

export function BulkRejectionProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<BulkRejectionJob[]>([])

  const addJob = useCallback((job: BulkRejectionJob) => {
    setJobs((prev) => [...prev, job])
  }, [])

  const updateJob = useCallback((progress: BulkRejectionProgressDto) => {
    setJobs((prev) =>
      prev.map((j) => (j.jobId === progress.jobId ? { ...j, ...progress } : j)),
    )
  }, [])

  const clearCompleted = useCallback(() => {
    setJobs((prev) =>
      prev.filter((j) => j.status !== "Completed" && j.status !== "Failed"),
    )
  }, [])

  const activeCount = jobs.filter(
    (j) => j.status === "Pending" || j.status === "InProgress",
  ).length

  return (
    <BulkRejectionContext.Provider
      value={{ jobs, activeCount, addJob, updateJob, clearCompleted }}
    >
      {children}
    </BulkRejectionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBulkRejectionContext(): BulkRejectionContextValue {
  const ctx = useContext(BulkRejectionContext)
  if (!ctx)
    throw new Error(
      "useBulkRejectionContext must be used inside <BulkRejectionProvider>",
    )
  return ctx
}
